import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Tooltip,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  EventAvailable as EventAvailableIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import AUTH_CONTEXT from "../../../store";
import { PhysioServices } from "../../../services/physio_services";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_USER_DETAILS } from "../../../base_routes_paths/data_paths";
import classes from "../Physio.module.css";

const VideoSessionsList = () => {
  const { token } = AUTH_CONTEXT();
  const navigate = useNavigate();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [data, setData] = useState([]);
  const [physioList, setPhysioList] = useState([]);

  // Doctor selection modal states
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [selectedSessionForReschedule, setSelectedSessionForReschedule] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isDoctorChanged, setIsDoctorChanged] = useState(false);

  // Text expansion modal states
  const [showTextModal, setShowTextModal] = useState(false);
  const [textModalContent, setTextModalContent] = useState({ title: "", content: "" });

  // Filter and sort states
  const [filters, setFilters] = useState({
    doctorName: "",
    status: "",
    slotType: "",
  });
  const [filteredData, setFilteredData] = useState([]);

  const isFetchingRef = useRef(false);

  // Helper function to get doctor name by ID
  const getDoctorNameById = (doctorId, physioListData) => {
    if (!doctorId || !physioListData || physioListData.length === 0) {
      console.log("getDoctorNameById: Missing data", { doctorId, physioListDataLength: physioListData?.length });
      return null;
    }
    
    console.log("Looking for doctor ID:", doctorId, "in physio list:", physioListData);
    
    const physio = physioListData.find(p => {
      const physioId = p.PhysioUserID?._id;
      console.log("Comparing:", physioId, "with", doctorId);
      return physioId === doctorId;
    });
    
    const doctorName = physio ? physio.PhysioUserID.Name : null;
    console.log("Found doctor name:", doctorName, "for ID:", doctorId);
    
    return doctorName;
  };

  // Helper function to convert UTC to local date only
  const convertUTCToLocalDate = (utcDateString) => {
    const utcDate = new Date(utcDateString);
    return utcDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  // Helper function to convert UTC to local time only
  const convertUTCToLocalTime = (utcDateString) => {
    const utcDate = new Date(utcDateString);
    return utcDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Helper function to convert seconds to minutes
  const convertSecondsToMinutes = (seconds) => {
    if (!seconds || seconds === 0) return "0 min";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} min`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Helper function to get session status
  const getSessionStatus = (session) => {
    if (session.IsCompleted) return "completed";
    if (session.IsReady) return "booked";
    return "initiated";
  };

  // Filter functions
  const applyFilters = useCallback((dataToFilter) => {
    let filtered = [...dataToFilter];

    // Filter by doctor name
    if (filters.doctorName) {
      filtered = filtered.filter(item => 
        item.doctorName && 
        item.doctorName.toLowerCase().includes(filters.doctorName.toLowerCase())
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(item => item.sessionStatus === filters.status);
    }

    // Filter by slot type
    if (filters.slotType) {
      if (filters.slotType === "free") {
        filtered = filtered.filter(item => item.IsFreeSlot === true);
      } else if (filters.slotType === "paid") {
        filtered = filtered.filter(item => item.IsFreeSlot === false);
      }
    }

    return filtered;
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      doctorName: "",
      status: "",
      slotType: "",
    });
  };

  // Get unique doctor names for filter dropdown
  const getUniqueDoctorNames = useCallback(() => {
    const names = data
      .map(item => item.doctorName)
      .filter(name => name && name !== "Unknown")
      .filter((name, index, arr) => arr.indexOf(name) === index)
      .sort();
    return names;
  }, [data]);

  // Apply filters whenever data or filters change
  useEffect(() => {
    const filtered = applyFilters(data);
    setFilteredData(filtered);
  }, [data, applyFilters]);

  const fetchPhysioList = useCallback(async () => {
    try {
      console.log("Fetching physio list...");
      const result = await PhysioServices.getAllPhysioList(token, navigate);
      
      console.log("Physio list API response:", result);
      
      if (result.status === 200) {
        console.log("Physio list fetched successfully:", result.data.PhysioList);
        setPhysioList(result.data.PhysioList);
        return result.data.PhysioList; // Return the list for immediate use
      } else {
        console.error("Failed to fetch physio list:", result);
        return [];
      }
    } catch (error) {
      console.error("Error fetching physio list:", error);
      return [];
    }
  }, [token, navigate]);

  const fetchVideoSessionList = useCallback(async (physioListData = null) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log("fetchVideoSessionList called but already fetching, skipping...");
      return;
    }

    console.log("fetchVideoSessionList called with physioListData:", physioListData?.length);
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const result = await PhysioServices.getVideoSessionList(token, navigate);

      console.log("API Response:", result);

      if (result.status === 200) {
        // Log the first item to see available fields
        if (result.data.videoSessionList && result.data.videoSessionList.length > 0) {
          console.log("First video session item keys:", Object.keys(result.data.videoSessionList[0]));
          console.log("First video session item:", result.data.videoSessionList[0]);
        }
        
        const currentPhysioList = physioListData || physioList;
        console.log("Using physio list for matching:", currentPhysioList?.length);
        
        const sessionData = result.data.videoSessionList.map((item, index) => {
          // Check multiple possible field names for doctor ID
          const doctorId = item.DoctorID || item.DoctorUserID || item.PhysioID || item.PhysioUserID;
          console.log("Processing session", index, "with doctor ID:", doctorId);
          
          return {
            id: item._id,
            index: index + 1,
            ...item,
            // Add computed fields
            sessionStatus: getSessionStatus(item),
            sessionDate: convertUTCToLocalDate(item.SessionStartTime),
            localStartTime: convertUTCToLocalTime(item.SessionStartTime),
            localEndTime: convertUTCToLocalTime(item.SessionEndTime),
            durationInMinutes: convertSecondsToMinutes(item.SessionDuration),
            actualDurationInMinutes: convertSecondsToMinutes(item.SessionActualDuration),
            // Add doctor name if available
            doctorName: getDoctorNameById(doctorId, currentPhysioList),
          };
        });
        
        console.log("Processed data:", sessionData);
        setData(sessionData);
        setIsDataFetched(true);
        toast.success(`Video sessions fetched successfully! (${sessionData.length} sessions)`, {
          position: "bottom-left",
          autoClose: 2000,
        });
      } else {
        console.error("API Error:", result);
        toast.error(`API Error: ${result.error || 'Unknown error'}`, {
          position: "bottom-left",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [token, navigate, physioList]);

  useEffect(() => {
    // First fetch physio list, then video sessions
    if (!isDataFetched && !isFetchingRef.current) {
      console.log("Calling fetchPhysioList and fetchVideoSessionList from useEffect");
      fetchPhysioList().then((physioListData) => {
        console.log("Physio list fetched, now calling video sessions with data:", physioListData?.length);
        fetchVideoSessionList(physioListData);
      });
    }
  }, [isDataFetched, fetchPhysioList, fetchVideoSessionList]);

  // Click handlers for navigation
  const handleUserClick = (userId) => {
    console.log("Navigating to user details:", userId);
    // Navigate to user details page
    navigate(`${BASE_PATH_TO_USER_DETAILS}/${userId}`);
  };

  const handleReschedule = (sessionId, sessionData) => {
    console.log("Reschedule clicked for session:", sessionId, sessionData);
    
    // Check if it's a paid session (IsFreeSlot = false)
    if (!sessionData.IsFreeSlot) {
      // For paid sessions, redirect to slots page with video session ID
      const doctorId = sessionData.DoctorID || sessionData.DoctorUserID || sessionData.PhysioID || sessionData.PhysioUserID;
      const doctorName = sessionData.doctorName || 'Doctor';
      
      if (doctorId) {
        // Encode doctor name for URL
        const encodedDoctorName = encodeURIComponent(doctorName);
        navigate(`/physio/slots/${doctorId}?reschedule=true&videoSessionId=${sessionId}&sessionType=paid&name=${encodedDoctorName}`);
      } else {
        toast.error("Doctor ID not found for this session", {
          position: "bottom-left",
          autoClose: 3000,
        });
      }
    } else {
      // For free sessions, show doctor selection modal first
      setSelectedSessionForReschedule({ sessionId, sessionData });
      
      // Pre-select the current doctor by default
      const currentDoctorId = sessionData.DoctorID || sessionData.DoctorUserID || sessionData.PhysioID || sessionData.PhysioUserID;
      const currentDoctor = {
        DoctorUserID: currentDoctorId,
        Name: sessionData.doctorName || 'Current Doctor',
        ProfileImage: ''
      };
      setSelectedDoctor(currentDoctor);
      setIsDoctorChanged(false);
      
      fetchDoctorsList();
      setShowDoctorModal(true);
    }
  };

  // Fetch doctors list for free slot reschedule
  const fetchDoctorsList = async () => {
    try {
      setIsLoadingDoctors(true);
      const result = await PhysioServices.getDoctorIdsAndNames(token, navigate);
      
      if (result.status === 200) {
        console.log("Doctors list fetched:", result.data.result);
        setDoctorsList(result.data.result);
      } else {
        toast.error(`Failed to fetch doctors list: ${result.error || 'Unknown error'}`, {
          position: "bottom-left",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error fetching doctors list:", error);
      toast.error(`Error fetching doctors list: ${error.message}`, {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Handle doctor selection for free slot reschedule
  const handleDoctorSelection = (doctor) => {
    const { sessionData } = selectedSessionForReschedule;
    const currentDoctorId = sessionData.DoctorID || sessionData.DoctorUserID || sessionData.PhysioID || sessionData.PhysioUserID;
    const newDoctorId = doctor.DoctorUserID;
    const doctorChanged = currentDoctorId !== newDoctorId;
    
    setSelectedDoctor(doctor);
    setIsDoctorChanged(doctorChanged);
    
    console.log("Doctor selected:", {
      currentDoctorId,
      newDoctorId,
      doctorChanged,
      doctor
    });
  };

  // Handle keeping the same doctor for free slot reschedule
  const handleKeepSameDoctor = () => {
    const { sessionData } = selectedSessionForReschedule;
    const currentDoctorId = sessionData.DoctorID || sessionData.DoctorUserID || sessionData.PhysioID || sessionData.PhysioUserID;
    
    // Create a doctor object for the current doctor
    const currentDoctor = {
      DoctorUserID: currentDoctorId,
      Name: sessionData.doctorName || 'Current Doctor',
      ProfileImage: ''
    };
    
    setSelectedDoctor(currentDoctor);
    setIsDoctorChanged(false);
    
    console.log("Keeping same doctor:", currentDoctorId);
  };

  // Handle proceeding with selected doctor
  const handleProceedWithDoctor = () => {
    if (!selectedDoctor) {
      toast.error("Please select a doctor to proceed", {
        position: "bottom-left",
        autoClose: 3000,
      });
      return;
    }

    const { sessionId } = selectedSessionForReschedule;
    const { sessionData } = selectedSessionForReschedule;
    const currentDoctorId = sessionData.DoctorID || sessionData.DoctorUserID || sessionData.PhysioID || sessionData.PhysioUserID;
    const doctorName = selectedDoctor.Name || 'Doctor';
    
    // Close modal and navigate to slots page
    setShowDoctorModal(false);
    setSelectedSessionForReschedule(null);
    setSelectedDoctor(null);
    setIsDoctorChanged(false);
    
    // Navigate to the selected doctor's slots page with doctor name
    const encodedDoctorName = encodeURIComponent(doctorName);
    navigate(`/physio/slots/${selectedDoctor.DoctorUserID}?reschedule=true&videoSessionId=${sessionId}&sessionType=free&isDoctorChanged=${isDoctorChanged}&newDoctorId=${selectedDoctor.DoctorUserID}&originalDoctorId=${currentDoctorId}&name=${encodedDoctorName}`);
  };

  // Handle closing doctor selection modal
  const handleCloseDoctorModal = () => {
    setShowDoctorModal(false);
    setSelectedSessionForReschedule(null);
    setDoctorsList([]);
    setSelectedDoctor(null);
    setIsDoctorChanged(false);
  };

  // Handle opening text modal for full content viewing
  const handleOpenTextModal = (title, content) => {
    setTextModalContent({ title, content });
    setShowTextModal(true);
  };

  // Handle closing text modal
  const handleCloseTextModal = () => {
    setShowTextModal(false);
    setTextModalContent({ title: "", content: "" });
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 70,
    },
    {
      field: "UserID",
      headerName: "User ID",
      width: 220,
      renderCell: (params) => {
        // Check multiple possible field names for user ID
        const userId = params.row.UserID || params.row.UserId || params.row.userId || params.row.user_id;
        
        if (!userId) {
          return (
            <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic" }}>
              N/A
            </Typography>
          );
        }
        
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": {
                color: "#1565c0",
                textDecoration: "underline",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              }
            }}
            onClick={() => handleUserClick(userId)}
            title="Click to view user details"
          >
            {userId}
          </Typography>
        );
      },
    },
    {
      field: "doctorName",
      headerName: "Doctor Name",
      width: 200,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            color: params.value ? "#333" : "#64748b",
            fontStyle: params.value ? "normal" : "italic"
          }}
        >
          {params.value || "Unknown"}
        </Typography>
      ),
    },
    {
      field: "sessionDate",
      headerName: "Date",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "localStartTime",
      headerName: "Start Time",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: "#64748b" }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "localEndTime",
      headerName: "End Time",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: "#64748b" }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "sessionStatus",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          size="small"
          icon={
            params.value === "completed" ? (
              <CheckCircleIcon sx={{ fontSize: 16 }} />
            ) : params.value === "booked" ? (
              <EventAvailableIcon sx={{ fontSize: 16 }} />
            ) : (
              <ScheduleIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{ 
            fontWeight: 500,
            backgroundColor: 
              params.value === "completed" ? "#d4edda" :
              params.value === "booked" ? "#cce5ff" :
              "#fff3cd",
            color:
              params.value === "completed" ? "#155724" :
              params.value === "booked" ? "#004085" :
              "#856404"
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      renderCell: (params) => {
        // Show reschedule button only if IsReady is true AND IsCompleted is false
        if (params.row.IsReady && !params.row.IsCompleted) {
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon sx={{ fontSize: 8 }} />}
              onClick={() => handleReschedule(params.row.id, params.row)}
              sx={{
                fontSize: "11px",
                padding: "2px 6px",
                minWidth: "80px",
                height: "24px",
                borderColor: "#1976d2",
                color: "#1976d2",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#1565c0",
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                }
              }}
            >
              Reschedule
            </Button>
          );
        }
        return (
          <Typography variant="body2" sx={{ color: "#9e9e9e", fontStyle: "italic" }}>
            -
          </Typography>
        );
      },
    },
    {
      field: "IsFreeSlot",
      headerName: "Slot Type",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "FREE" : "PAID"}
          size="small"
          sx={{
            backgroundColor: params.value ? "#d4edda" : "#cce5ff",
            color: params.value ? "#155724" : "#004085",
            fontWeight: 500,
            fontSize: "12px",
          }}
        />
      ),
    },
    {
      field: "durationInMinutes",
      headerName: "Duration",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: "actualDurationInMinutes",
      headerName: "Actual Duration",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: "RatingForDoctor",
      headerName: "Rating",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StarIcon 
            sx={{ 
              fontSize: 16, 
              color: params.value > 0 ? "#fbbf24" : "#d1d5db" 
            }} 
          />
          <Typography variant="body2">
            {params.value > 0 ? `${params.value}/5` : "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "PainLevelBeforeSession",
      headerName: "Pain Before",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "PainLevelAfterSession",
      headerName: "Pain After",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "NotesByUser",
      headerName: "Notes",
      width: 200,
      renderCell: (params) => {
        const content = params.value || "-";
        const isLongText = content.length > 30;
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Tooltip title={isLongText ? "Click to view full text" : content}>
              <Typography 
                variant="body2" 
                sx={{ 
                  maxWidth: isLongText ? 150 : 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: isLongText ? "pointer" : "default",
                  "&:hover": isLongText ? { textDecoration: "underline" } : {}
                }}
                onClick={isLongText ? () => handleOpenTextModal("Notes", content) : undefined}
              >
                {content}
              </Typography>
            </Tooltip>
            {isLongText && (
              <IconButton
                size="small"
                onClick={() => handleOpenTextModal("Notes", content)}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: "FeedbackForDoctor",
      headerName: "Feedback",
      width: 200,
      renderCell: (params) => {
        const content = params.value || "-";
        const isLongText = content.length > 30;
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Tooltip title={isLongText ? "Click to view full text" : content}>
              <Typography 
                variant="body2" 
                sx={{ 
                  maxWidth: isLongText ? 150 : 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: isLongText ? "pointer" : "default",
                  "&:hover": isLongText ? { textDecoration: "underline" } : {}
                }}
                onClick={isLongText ? () => handleOpenTextModal("Feedback", content) : undefined}
              >
                {content}
              </Typography>
            </Tooltip>
            {isLongText && (
              <IconButton
                size="small"
                onClick={() => handleOpenTextModal("Feedback", content)}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  function CustomToolbar() {
    const uniqueDoctorNames = getUniqueDoctorNames();
    const hasActiveFilters = filters.doctorName || filters.status || filters.slotType;

    return (
      <GridToolbarContainer
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* Left side - Filters */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Doctor Name Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Doctor</InputLabel>
            <Select
              value={filters.doctorName}
              label="Doctor"
              onChange={(e) => handleFilterChange("doctorName", e.target.value)}
            >
              <MenuItem value="">All Doctors</MenuItem>
              {uniqueDoctorNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="booked">Booked</MenuItem>
              <MenuItem value="initiated">Initiated</MenuItem>
            </Select>
          </FormControl>

          {/* Slot Type Filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.slotType}
              label="Type"
              onChange={(e) => handleFilterChange("slotType", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              sx={{ 
                textTransform: "none",
                color: "#666",
                "&:hover": {
                  backgroundColor: "#f5f5f5"
                }
              }}
            >
              Clear
            </Button>
          )}

          {/* Results Count */}
          {hasActiveFilters && (
            <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
              {filteredData.length} of {data.length} sessions
            </Typography>
          )}
        </Box>

        {/* Right side - Export */}
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ fileName: "VideoSessionsList" }}
          excelOptions={{ fileName: "VideoSessionsList" }}
        />
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 2 }}>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_HOME}>
          Dashboard
        </Link>
        <Typography color="text.primary">Physio Services</Typography>
        <Typography color="text.primary">Video Sessions</Typography>
      </Breadcrumbs>

      <DataGridPremium
        className={classes.dataGrid}
        slots={{
          toolbar: CustomToolbar,
        }}
        rows={filteredData}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        disableColumnSorting
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        columnSpacing={60}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />

      {/* Doctor Selection Modal for Free Slot Reschedule */}
      <Dialog
        open={showDoctorModal}
        onClose={handleCloseDoctorModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Doctor for Free Session Reschedule
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can keep the same doctor or choose a different one
          </Typography>
        </DialogTitle>
        <DialogContent>
          {isLoadingDoctors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Option to keep same doctor */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  Current Doctor (Keep Same)
                </Typography>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleKeepSameDoctor}
                    selected={selectedDoctor && !isDoctorChanged}
                    sx={{
                      borderRadius: 1,
                      border: selectedDoctor && !isDoctorChanged ? '2px solid #1976d2' : '2px solid #e0e0e0',
                      backgroundColor: selectedDoctor && !isDoctorChanged ? '#f3f8ff' : '#fafafa',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2',
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={selectedSessionForReschedule?.sessionData?.doctorName ? '' : ''}
                        sx={{ 
                          width: 48, 
                          height: 48, 
                          bgcolor: selectedDoctor && !isDoctorChanged ? '#1976d2' : '#666'
                        }}
                      >
                        {selectedSessionForReschedule?.sessionData?.doctorName?.charAt(0)?.toUpperCase() || 'D'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={selectedSessionForReschedule?.sessionData?.doctorName || 'Current Doctor'}
                      secondary="Keep the same doctor for this session"
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: selectedDoctor && !isDoctorChanged ? '#1976d2' : '#333'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '14px',
                        color: '#666'
                      }}
                    />
                    {selectedDoctor && !isDoctorChanged && (
                      <Box sx={{ ml: 2 }}>
                        <Chip
                          label="Selected"
                          size="small"
                          sx={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              </Box>

              {/* Option to select different doctor */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Or Choose a Different Doctor
              </Typography>
              <List>
                {doctorsList.map((doctor) => {
                  // Don't show current doctor in the list again
                  const currentDoctorId = selectedSessionForReschedule?.sessionData?.DoctorID || 
                                        selectedSessionForReschedule?.sessionData?.DoctorUserID || 
                                        selectedSessionForReschedule?.sessionData?.PhysioID || 
                                        selectedSessionForReschedule?.sessionData?.PhysioUserID;
                  
                  if (doctor.DoctorUserID === currentDoctorId) {
                    return null; // Skip current doctor
                  }

                  const isSelected = selectedDoctor && selectedDoctor.DoctorUserID === doctor.DoctorUserID;

                  return (
                    <ListItem key={doctor.DoctorUserID} disablePadding>
                      <ListItemButton
                        onClick={() => handleDoctorSelection(doctor)}
                        selected={isSelected}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          backgroundColor: isSelected ? '#f3f8ff' : 'white',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#1976d2',
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={doctor.ProfileImage}
                            alt={doctor.Name}
                            sx={{ width: 48, height: 48 }}
                          >
                            {doctor.Name.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={doctor.Name}
                          secondary={`Doctor ID: ${doctor.DoctorUserID}`}
                          primaryTypographyProps={{
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: '16px',
                            color: isSelected ? '#1976d2' : '#333'
                          }}
                          secondaryTypographyProps={{
                            fontSize: '14px',
                            color: '#666'
                          }}
                        />
                        {isSelected && (
                          <Box sx={{ ml: 2 }}>
                            <Chip
                              label="Selected"
                              size="small"
                              sx={{
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDoctorModal} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleProceedWithDoctor}
            variant="contained"
            color="primary"
            disabled={!selectedDoctor}
            sx={{ ml: 1, color: 'white' }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Text Expansion Modal */}
      <Dialog
        open={showTextModal}
        onClose={handleCloseTextModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {textModalContent.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
              padding: "8px 0"
            }}
          >
            {textModalContent.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTextModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoSessionsList;